import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, fmtDKK } from "@/components/shared";
import { ChartBox } from "@/components/ChartBox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress as ProgressBar } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line } from "recharts";
import {
  Building2, Users, Dog, GraduationCap, TrendingUp, CalendarCheck, ClipboardCheck, ShieldCheck,
  BadgeCheck, Wallet, Coins, Download, FileText, AlertTriangle, Sparkles, Activity,
} from "lucide-react";
import { toast } from "sonner";

const COLORS = ["hsl(28 33% 48%)", "hsl(34 40% 62%)", "hsl(150 30% 45%)", "hsl(38 55% 55%)", "hsl(0 55% 58%)", "hsl(210 40% 55%)"];

export default function Analytics() {
  const { user } = useAuth();
  const isGlobal = ["super_admin", "head_office_admin"].includes(user?.role);
  const isTrainer = user?.role === "trainer";
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [filters, setFilters] = useState({ location_id: "all", program_id: "all", trainer_id: "all", level: "all" });

  const load = useCallback(async () => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
    try { const r = await api.get("/analytics", { params }); setD(r.data); }
    catch (e) { if (e?.response?.status === 403) setErr(true); }
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const params = filters.location_id !== "all" ? { location_id: filters.location_id } : {};
      const r = await api.get("/analytics/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "hv_analytics.csv"; a.click();
      URL.revokeObjectURL(url); toast.success("CSV exported");
    } catch { toast.error("Export failed"); }
  };

  if (err) return <div className="text-center py-20 text-muted-foreground" data-testid="analytics-no-access">HV Analytics is not available for your role.</div>;
  if (!d) return <div className="text-center py-20 text-muted-foreground">Loading analytics…</div>;

  if (d.scope === "trainer") return <TrainerAnalytics d={d} />;

  const k = d.kpis, c = d.charts;
  return (
    <div>
      <PageHeader
        title="HV Analytics"
        subtitle={`Strategic performance intelligence ${d.scope === "network" ? "across the Hundeverket network" : "for your location"}.`}
        action={
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="gap-1.5" onClick={exportCsv} data-testid="analytics-export-csv"><Download size={15} /> CSV</Button>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => toast.info("PDF export coming soon")} data-testid="analytics-export-pdf"><FileText size={15} /> PDF</Button>
          </div>
        }
      />

      {/* Filters */}
      <Card className="border-border mb-6"><CardContent className="p-3 flex flex-wrap gap-2 items-center">
        <span className="text-xs uppercase tracking-wide font-semibold text-muted-foreground mr-1">Filters</span>
        {isGlobal && <FilterSelect label="Location" value={filters.location_id} onChange={(v) => setFilters({ ...filters, location_id: v })} options={d.filters.locations} testid="filter-location" />}
        <FilterSelect label="Program" value={filters.program_id} onChange={(v) => setFilters({ ...filters, program_id: v })} options={d.filters.programs} testid="filter-program" />
        <FilterSelect label="Trainer" value={filters.trainer_id} onChange={(v) => setFilters({ ...filters, trainer_id: v })} options={d.filters.trainers} testid="filter-trainer" />
        <FilterSelect label="Level" value={filters.level} onChange={(v) => setFilters({ ...filters, level: v })} options={(d.filters.levels || []).map((l) => ({ id: l, name: l }))} testid="filter-level" capitalize />
        {Object.values(filters).some((v) => v !== "all") && <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => setFilters({ location_id: "all", program_id: "all", trainer_id: "all", level: "all" })}>Reset</Button>}
      </CardContent></Card>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {isGlobal && <Kpi icon={Building2} n={k.active_locations} label="Active Locations" />}
        <Kpi icon={Users} n={k.active_clients} label="Active Clients" sub={`+${k.new_clients_month} this month`} />
        <Kpi icon={Dog} n={k.active_dogs} label="Active Dogs" sub={`+${k.new_dogs_month} this month`} />
        <Kpi icon={GraduationCap} n={k.active_trainers} label="Active Trainers" />
        <Kpi icon={CalendarCheck} n={k.completed_classes} label="Classes Completed" sub={`${k.upcoming_classes} upcoming`} />
        <Kpi icon={ClipboardCheck} n={`${k.attendance_rate}%`} label="Attendance Rate" tone="text-emerald-700" />
        <Kpi icon={ClipboardCheck} n={`${k.homework_completion}%`} label="Homework Completion" />
        <Kpi icon={ShieldCheck} n={k.quality_score} label="Quality Score" tone={k.quality_score >= 75 ? "text-green-700" : "text-amber-700"} />
        <Kpi icon={BadgeCheck} n={`${k.certified_trainer_rate}%`} label="Certified Trainers" />
        <Kpi icon={Wallet} n={fmtDKK(k.revenue)} label="Revenue" tone="text-emerald-700" />
        <Kpi icon={Coins} n={fmtDKK(k.royalty_due)} label="Royalty Due" tone="text-orange-700" />
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="h-auto flex-wrap">
          <TabsTrigger value="overview" data-testid="an-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="dogs" data-testid="an-tab-dogs">Dogs</TabsTrigger>
          <TabsTrigger value="classes" data-testid="an-tab-classes">Classes</TabsTrigger>
          <TabsTrigger value="method" data-testid="an-tab-method">HV Method</TabsTrigger>
          <TabsTrigger value="quality" data-testid="an-tab-quality">Quality</TabsTrigger>
          <TabsTrigger value="academy" data-testid="an-tab-academy">Academy</TabsTrigger>
          <TabsTrigger value="payments" data-testid="an-tab-payments">Payments</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <div className="grid lg:grid-cols-2 gap-4">
            {d.scope === "network" && <BarCard title="Dogs by Location" data={c.dogs_by_location} />}
            {d.scope === "network" && <BarCard title="Clients by Location" data={c.clients_by_location} />}
            <BarCard title="Classes Completed by Location" data={c.classes_completed_by_location} />
            <PctCard title="Attendance Rate by Location" data={c.attendance_by_location} />
            <PctCard title="Homework Completion by Location" data={c.homework_by_location} />
            <PctCard title="Quality Score by Location" data={c.quality_by_location} />
            <PieCard title="Trainer Certification" data={c.trainer_certification} />
            <PieCard title="Dogs by Training Level" data={c.dogs_by_level} />
            <BarCard title="Revenue by Location" data={c.revenue_by_location} money />
            <LineCard title="New Dogs — Monthly Growth" data={c.growth_trend} />
          </div>
        </TabsContent>

        <TabsContent value="dogs" className="mt-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <Kpi icon={Dog} n={d.dogs.no_trainer} label="No Assigned Trainer" tone="text-amber-700" />
            <Kpi icon={FileText} n={d.dogs.incomplete_profiles} label="Incomplete Profiles" />
            <Kpi icon={TrendingUp} n={d.dogs.ready_next} label="Ready for Next Level" tone="text-green-700" />
            <Kpi icon={AlertTriangle} n={d.dogs.behaviour_flags} label="Behaviour Flags" tone="text-red-700" />
          </div>
          <div className="grid lg:grid-cols-2 gap-4"><PieCard title="Dogs by Level" data={d.dogs.by_level} /></div>
        </TabsContent>

        <TabsContent value="classes" className="mt-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <Kpi icon={CalendarCheck} n={d.classes.upcoming} label="Upcoming" />
            <Kpi icon={CalendarCheck} n={d.classes.completed} label="Completed" tone="text-green-700" />
            <Kpi icon={CalendarCheck} n={d.classes.cancelled} label="Cancelled" tone="text-red-700" />
            <Kpi icon={Activity} n={`${d.classes.avg_capacity_usage}%`} label="Avg Capacity" />
            <Kpi icon={AlertTriangle} n={`${d.classes.no_show_rate}%`} label="No-show Rate" tone="text-amber-700" />
            <Kpi icon={CalendarCheck} n={d.classes.full} label="Full Classes" />
          </div>
          <div className="grid lg:grid-cols-2 gap-4">
            <PctCard title="Attendance by Program" data={d.classes.attendance_by_program} />
            <BarCard title="Most Popular Programs" data={d.classes.popular_programs} />
          </div>
        </TabsContent>

        <TabsContent value="method" className="mt-4">
          <TableCard head={["Program", "Classes", "Dogs Enrolled", "Attendance", "Homework"]}>
            {d.method.map((m) => (
              <tr key={m.name} className="border-b border-border/50" data-testid="method-row">
                <td className="px-4 py-3 font-medium">{m.name}</td>
                <td className="px-4 py-3">{m.classes}</td>
                <td className="px-4 py-3">{m.dogs_enrolled}</td>
                <td className="px-4 py-3">{m.attendance_rate}%</td>
                <td className="px-4 py-3">{m.homework_completion}%</td>
              </tr>
            ))}
            {d.method.length === 0 && <EmptyRow cols={5} text="No program activity yet." />}
          </TableCard>
        </TabsContent>

        <TabsContent value="quality" className="mt-4">
          <div className="grid lg:grid-cols-2 gap-4">
            <PctCard title="Quality Score by Location" data={d.quality.score_by_location} />
            <PieCard title="Open Issues by Severity" data={d.quality.open_by_severity} />
            <PieCard title="Issues by Type" data={d.quality.by_type} />
            <Card className="border-border"><CardHeader><CardTitle className="font-heading text-base flex items-center gap-2"><AlertTriangle size={16} className="text-amber-600" /> Locations Needing Attention</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {d.quality.needing_attention.map((l) => (<div key={l.name} className="flex items-center justify-between text-sm" data-testid="quality-attention"><span>{l.name}</span><span className="flex items-center gap-2 text-muted-foreground">{l.open_issues} open · <StatusBadge value={l.score >= 60 ? "in_progress" : "overdue"} /> {l.score}</span></div>))}
                {d.quality.needing_attention.length === 0 && <p className="text-sm text-muted-foreground py-3 text-center">All locations healthy. Certification gaps: {d.quality.certification_gaps}.</p>}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="academy" className="mt-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <Kpi icon={BadgeCheck} n={d.academy.certified} label="Certified" tone="text-green-700" />
            <Kpi icon={GraduationCap} n={d.academy.in_progress} label="In Progress" tone="text-amber-700" />
            <Kpi icon={GraduationCap} n={d.academy.not_started} label="Not Started" />
            <Kpi icon={TrendingUp} n={`${d.academy.completion_rate}%`} label="Completion Rate" />
          </div>
          <Card className="border-border"><CardHeader><CardTitle className="font-heading text-base">Academy Progress by Location</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {d.academy.by_location.map((l) => (<div key={l.name} data-testid="academy-loc"><div className="flex justify-between text-sm mb-1"><span>{l.name}</span><span className="text-muted-foreground">{l.completed}/{l.total} · {l.rate}%</span></div><ProgressBar value={l.rate} className="h-2" /></div>))}
              {d.academy.by_location.length === 0 && <p className="text-sm text-muted-foreground py-3 text-center">No academy enrollments.</p>}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payments" className="mt-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <Kpi icon={Wallet} n={fmtDKK(k.revenue)} label="Revenue" tone="text-emerald-700" />
            <Kpi icon={BadgeCheck} n={d.payments.membership_count} label="Memberships" />
            <Kpi icon={FileText} n={d.payments.package_count} label="Packages" />
            <Kpi icon={Coins} n={fmtDKK(d.payments.franchise_fees_due)} label="Fees Due" tone="text-amber-700" />
          </div>
          <div className="grid lg:grid-cols-2 gap-4">
            <BarCard title="Revenue by Location" data={d.payments.revenue_by_location} money />
            <BarCard title="Revenue by Product" data={d.payments.revenue_by_product} money />
            <Card className="border-border"><CardHeader><CardTitle className="font-heading text-base">Paid vs Unpaid Bookings</CardTitle></CardHeader>
              <CardContent className="flex gap-4"><div className="flex-1 text-center rounded-lg bg-green-50 py-4"><div className="text-2xl font-heading font-semibold text-green-700">{d.payments.paid_vs_unpaid.paid}</div><div className="text-xs text-muted-foreground">Paid</div></div><div className="flex-1 text-center rounded-lg bg-amber-50 py-4"><div className="text-2xl font-heading font-semibold text-amber-700">{d.payments.paid_vs_unpaid.unpaid}</div><div className="text-xs text-muted-foreground">Unpaid</div></div></CardContent>
            </Card>
            <Card className="border-border"><CardHeader><CardTitle className="font-heading text-base">Royalty Due by Location</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {d.payments.royalty_due_by_location.map((r) => (<div key={r.name} className="flex items-center justify-between text-sm" data-testid="royalty-loc"><span>{r.name}</span><span className="flex items-center gap-2">{fmtDKK(r.due)} <StatusBadge value={r.status} /></span></div>))}
                {d.payments.royalty_due_by_location.length === 0 && <p className="text-sm text-muted-foreground py-3 text-center">No royalty data.</p>}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------- Trainer "My Performance" ---------- */
function TrainerAnalytics({ d }) {
  const k = d.kpis;
  return (
    <div>
      <PageHeader title="My Performance" subtitle="Your personal training analytics — assigned dogs, classes and follow-ups." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Kpi icon={Dog} n={k.assigned_dogs} label="Assigned Dogs" />
        <Kpi icon={CalendarCheck} n={k.my_classes_month} label="My Classes" />
        <Kpi icon={ClipboardCheck} n={`${k.attendance_rate}%`} label="Attendance Rate" tone="text-emerald-700" />
        <Kpi icon={ClipboardCheck} n={`${k.homework_completed}/${k.homework_assigned}`} label="Homework Done" />
        <Kpi icon={TrendingUp} n={k.dogs_ready_next} label="Ready for Next Level" tone="text-green-700" />
        <Kpi icon={AlertTriangle} n={k.dogs_needing_attention} label="Needing Attention" tone="text-amber-700" />
        <Kpi icon={FileText} n={k.progress_notes} label="Progress Notes" />
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <PieCard title="My Dogs by Level" data={d.charts.dogs_by_level} />
        <BarCard title="My Classes by Program" data={d.charts.classes_by_program} />
        <Card className="border-border"><CardHeader><CardTitle className="font-heading text-base flex items-center gap-2"><ClipboardCheck size={16} /> Homework Follow-up</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {d.homework_followup.map((h) => (<div key={`${h.dog}-${h.title}`} className="flex items-center justify-between text-sm" data-testid="tr-hw-followup"><div><span className="font-medium">{h.dog}</span> — {h.title}</div><StatusBadge value={h.status} /></div>))}
            {d.homework_followup.length === 0 && <p className="text-sm text-muted-foreground py-3 text-center">All homework completed 🎉</p>}
          </CardContent>
        </Card>
        <Card className="border-border"><CardHeader><CardTitle className="font-heading text-base flex items-center gap-2"><AlertTriangle size={16} className="text-red-600" /> Dogs with Behaviour Flags</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {d.behaviour_flag_dogs.map((dg) => (<div key={dg.name} className="flex items-center justify-between text-sm" data-testid="tr-flag-dog"><span className="font-medium">{dg.name}</span><span className="text-xs text-muted-foreground">{(dg.flags || []).join(", ") || "flagged"}</span></div>))}
            {d.behaviour_flag_dogs.length === 0 && <p className="text-sm text-muted-foreground py-3 text-center">No behaviour flags.</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/* ---------- reusable ---------- */
const Kpi = ({ icon: Icon, n, label, sub, tone }) => (
  <Card className="border-border"><CardContent className="p-4">
    <div className="flex items-center gap-2 text-muted-foreground mb-1.5"><Icon size={15} strokeWidth={1.5} /><span className="text-[11px] uppercase tracking-wide font-semibold">{label}</span></div>
    <div className={`text-xl font-heading font-semibold tracking-tight ${tone || ""}`}>{n}</div>
    {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
  </CardContent></Card>
);

function FilterSelect({ label, value, onChange, options, testid, capitalize }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={`h-8 w-40 ${capitalize ? "capitalize" : ""}`} data-testid={testid}><SelectValue placeholder={label} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All {label}s</SelectItem>
        {(options || []).map((o) => <SelectItem key={o.id} value={o.id} className={capitalize ? "capitalize" : ""}>{o.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function BarCard({ title, data = [], money }) {
  return (
    <Card className="border-border"><CardHeader><CardTitle className="font-heading text-base">{title}</CardTitle></CardHeader>
      <CardContent>
        {data.length === 0 ? <Empty /> : (
          <ChartBox height={240}>
            <BarChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: -12 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(34 22% 87%)" vertical={false} />
              <XAxis dataKey="name" fontSize={11} tickLine={false} axisLine={false} interval={0} />
              <YAxis fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip formatter={(v) => (money ? fmtDKK(v) : v)} cursor={{ fill: "hsl(34 24% 91%)" }} />
              <Bar dataKey="value" fill="hsl(28 33% 48%)" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ChartBox>
        )}
      </CardContent>
    </Card>
  );
}

function PctCard({ title, data = [] }) {
  return (
    <Card className="border-border"><CardHeader><CardTitle className="font-heading text-base">{title}</CardTitle></CardHeader>
      <CardContent className="space-y-3 pt-1">
        {data.length === 0 ? <Empty /> : data.map((r) => (
          <div key={r.name}><div className="flex justify-between text-sm mb-1"><span>{r.name}</span><span className="font-medium">{r.value}%</span></div><ProgressBar value={r.value} className="h-2" /></div>
        ))}
      </CardContent>
    </Card>
  );
}

function PieCard({ title, data = [] }) {
  const clean = (data || []).filter((x) => x.value > 0);
  return (
    <Card className="border-border"><CardHeader><CardTitle className="font-heading text-base">{title}</CardTitle></CardHeader>
      <CardContent>
        {clean.length === 0 ? <Empty /> : (
          <ChartBox height={240}>
            <PieChart>
              <Pie data={clean} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={(e) => e.name}>
                {clean.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ChartBox>
        )}
      </CardContent>
    </Card>
  );
}

function LineCard({ title, data = [] }) {
  return (
    <Card className="border-border"><CardHeader><CardTitle className="font-heading text-base">{title}</CardTitle></CardHeader>
      <CardContent>
        <ChartBox height={240}>
          <LineChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: -12 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(34 22% 87%)" vertical={false} />
            <XAxis dataKey="name" fontSize={11} tickLine={false} axisLine={false} />
            <YAxis fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
            <Tooltip cursor={{ fill: "hsl(34 24% 91%)" }} />
            <Line type="monotone" dataKey="value" stroke="hsl(28 33% 48%)" strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ChartBox>
      </CardContent>
    </Card>
  );
}

const Empty = () => <p className="text-sm text-muted-foreground py-8 text-center">No data for the selected filters.</p>;

function TableCard({ head, children }) {
  return (
    <Card className="border-border"><CardContent className="p-0 overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground"><tr>{head.map((h) => <th key={h} className="text-left font-semibold px-4 py-3">{h}</th>)}</tr></thead>
        <tbody>{children}</tbody>
      </table>
    </CardContent></Card>
  );
}
const EmptyRow = ({ cols, text }) => (<tr><td colSpan={cols} className="text-center text-muted-foreground py-10">{text}</td></tr>);
